'use client';

import { useEffect, useRef } from 'react';

interface ScriptedHtmlProps {
  /** The class name to apply */
  className?: string;
  /** The HTML content to render */
  html: string;
}

/**
 * Renders arbitrary HTML that includes <script> tags by re-creating each
 * script node via document.createElement so the browser actually executes them.
 * External scripts are sequentially loaded in document to preserve load-order dependencies
 */
const ScriptedHtml = ({ html, className }: ScriptedHtmlProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    // Parse the raw HTML string into a document fragment.
    const template = document.createElement('template');

    template.innerHTML = html;

    const fragment = template.content;

    // Separate non-script nodes from script nodes, preserving order.
    const scriptNodes: HTMLScriptElement[] = [];
    const nonScriptNodes: Node[] = [];

    fragment.childNodes.forEach(node => {
      if (
        node.nodeType === Node.ELEMENT_NODE &&
        (node as Element).tagName === 'SCRIPT'
      ) {
        scriptNodes.push(node as HTMLScriptElement);
      } else {
        nonScriptNodes.push(node.cloneNode(true));
      }
    });

    // Append non-script markup first so containers exist before scripts run.
    nonScriptNodes.forEach(node => container.appendChild(node));

    // Load external scripts sequentially.
    const loadScriptsSequentially = (
      scripts: HTMLScriptElement[],
      index: number,
    ): void => {
      if (index >= scripts.length) return;

      const original = scripts[index];
      const script = document.createElement('script');

      // Copy all attributes (src, type, async, defer, …)
      Array.from(original.attributes).forEach(attr => {
        script.setAttribute(attr.name, attr.value);
      });

      // Copy inline script content if any.
      if (original.textContent) {
        script.textContent = original.textContent;
      }

      const next = () => loadScriptsSequentially(scripts, index + 1);

      if (original.src) {
        script.onload = next;
        script.onerror = next; // Continue even if one script fails to load.
      }

      container.appendChild(script);

      if (!original.src) {
        next();
      }
    };

    loadScriptsSequentially(scriptNodes, 0);

    return () => {
      container.innerHTML = '';
    };
  }, [html]);

  return <div ref={containerRef} className={className} />;
};

export default ScriptedHtml;
